import ColorConverter from 'css-filter-converter';
import Resizer from 'react-image-file-resizer';
import slug from 'slug';

import { Defaults, ErrorMessages } from '../../constants';
import { FeaturedBanner, FeaturedBannerType } from '../../types/FeaturedBanner';
import { ImageFile } from '../../types/ImageFile';
import { Media, MediaDimension, ResizeImageOptions } from '../../types/Media';
import { Post } from '../../types/Post';
import { User } from '../../types/User';
/**
 * Run a promise-returning function and catch any exceptions.
 * Useful for centralizing error handling.
 *
 * @param cb The function to run.
 * @returns A tuple where the first element is the result of the function
 * and the second element is the error that occurred, if any.
 */
export async function catchError<Cb extends Promise<unknown>>(cb: Cb): Promise<[Awaited<Cb> | null, unknown | null]> {
  try {
    return [await cb, null];
  } catch (error) {
    return [null, error];
  }
}

/**
 * Run a fetch request and return the response and parsed JSON body.
 *
 * @param url The URL to fetch.
 * @param requestOptions The options to pass to the fetch function.
 * @returns A tuple where the first element is the response and the second element is the
 * parsed JSON body. If the fetch request fails, the response will be null, and if the
 * response body is not JSON, the body will be null.
 */
export async function getFetchResponse<Data>(url: string, requestOptions: RequestInit): Promise<[Response | null, Data | null]> {
  const [response] = await catchError(fetch(url, requestOptions));
  if (!response) return [null, null];

  const [body] = await catchError(response.json());
  return [response, body];
}

/**
 * Build a query string from a given object.
 *
 * @param data The object to turn into a query string.
 * @returns The query string.
 */
export function buildQueryString(data: Record<string, string | number | boolean>): string {
  if (!data) return '';
  const entries = Object.entries(data);
  const params = entries.map((item) => item.map((el) => encodeURIComponent(el)).join('='));
  return params.join('&');
}

/**
 * Convert a hex color to a CSS filter string.
 *
 * @param hex The hex color to convert.
 * @returns The CSS filter string.
 */
export function hexToFilter(hex: string): string {
  return ColorConverter.hexToFilter(hex)?.color || 'none';
}

export function removeUrlTrailingSlash(url = '') {
  return url.replace(/\/$/, '');
}

export function emptyStringToUndefined(value: string | undefined): string | undefined {
  return value === '' ? undefined : value;
}

export const getErrorMessage = (message: string, path: string) => ({
  message,
  path: [path],
});

export const isFieldRequired = (display: boolean, value: unknown) => !display || (typeof value === 'string' && value.length > 0);

export const fileListToImageFiles = (fileList: FileList): File[] => {
  return Array.from(fileList).filter((file) => {
    const mimeType = (file.type || '').toLowerCase();
    return mimeType.startsWith('image/');
  });
};

export function checkImageFile(resource: File | Blob) {
  const imageResourceRegex = /image\//gi;
  return !!imageResourceRegex.test(resource.type);
}

export const resizeImage = async (file: File | Blob, options: ResizeImageOptions) => {
  const { compressFormat = 'JPEG', dimensions } = options;

  const resize = (dimension: MediaDimension) =>
    new Promise<Blob>((resolve) => {
      Resizer.imageFileResizer(
        file,
        dimension.width,
        dimension.height,
        compressFormat,
        100,
        0,
        (response: any) => resolve(response as Blob),
        'blob',
      );
    });

  const files: Blob[] = [];

  for (const dimension of dimensions) {
    const newImage = await resize(dimension);
    files.push(newImage);
  }

  return files;
};

export const getFileImageHeightAndWidth = (src: string) => {
  return new Promise<{
    height: number;
    width: number;
  }>((resolve) => {
    const img = new Image();
    img.onload = function () {
      resolve({
        height: (this as HTMLImageElement).height,
        width: (this as HTMLImageElement).width,
      });
    };
    img.src = src;
  });
};

export const checkFileSize = (file: File) => {
  if (checkImageFile(file)) {
    if (file.size > Defaults.FILE_IMAGE_SIZE) {
      throw new Error(ErrorMessages.FILE_IMAGE_SIZE_LIMIT);
    }
    return true;
  }

  throw new Error(ErrorMessages.FILE_IMAGE_FORMAT);
};

export type SortDirection = 'asc' | 'desc' | undefined;

export function containsHTMLTags(content: string) {
  const htmlTagRegex = /<\/?([a-z][a-z0-9]*)\b[^>]*>/i;
  return htmlTagRegex.test(content);
}

/**
 * Convert the html contents into array
 *
 * @param htmlContent The content that has HTML tags.
 * @returns Array of html contents
 */
export function separateTagsIntoArray(htmlContent: string): string[] {
  const wrappedContent = `<div>${htmlContent}</div>`;
  const parser = new DOMParser();
  const doc = parser.parseFromString(wrappedContent, 'text/html');
  const wrapper = doc.body.firstElementChild;
  if (!wrapper) {
    return [];
  }
  return Array.from(wrapper.children).map((child) => child.outerHTML);
}

/**
 * Get the first image in html content
 *
 * @param htmlContent The content that has HTML tags.
 * @returns img tag
 */
export function getFirstImageSrc(htmlContent: string): string | null {
  const wrappedContent = `<div>${htmlContent}</div>`;
  const parser = new DOMParser();
  const doc = parser.parseFromString(wrappedContent, 'text/html');
  const firstImage = doc.querySelector('img');
  return firstImage ? firstImage.getAttribute('src') : null;
}

export function extractImageWidthValue(imgTag: string): number {
  const widthMatch = imgTag.match(/width="(\d+)"/);
  return widthMatch ? parseInt(widthMatch[1], 10) : 0;
}

/**
 * Extracts images from both gallery div (data-images) and regular img tags.
 * Removes all image elements from the HTML content.
 *
 * @param htmlContent The content that has HTML tags.
 * @returns Returns extracted images and cleaned HTML content.
 */
export function extractImagesFromPostContent(htmlContent: string): {
  images: ImageFile[];
  cleanedContent: string;
} {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  const extractedImages: ImageFile[] = [];

  // 1. Extract gallery images from data-images attribute
  const galleryDiv = tempDiv.querySelector('div[data-images]');
  if (galleryDiv) {
    const imagesData = galleryDiv.getAttribute('data-images');
    if (imagesData) {
      try {
        const galleryImages: ImageFile[] = JSON.parse(imagesData.replace(/&quot;/g, '"'));
        if (Array.isArray(galleryImages)) {
          extractedImages.push(...galleryImages);
        }
      } catch (e) {
        console.error('Error parsing data-images:', e);
      }
    }
    galleryDiv.remove();
  }

  // 2. Extract standalone images from img tags
  const imageElements = tempDiv.querySelectorAll('img');
  imageElements.forEach((img) => {
    extractedImages.push({
      src: img.getAttribute('src') || '',
      alt: img.getAttribute('alt') || '',
    });
    img.remove(); // Remove the image from the content
  });

  return {
    images: extractedImages,
    cleanedContent: tempDiv.innerHTML.trim(),
  };
}

export const getAuthorName = (data: any) => {
  return data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : data.username;
};

export function getVideoThumbnailUrl(media: Media): string | null {
  if (media.mediaConvertJob?.thumbnailUrl) {
    return media.mediaConvertJob.thumbnailUrl;
  }
  return null;
}

export function getBannerImage(banner: FeaturedBanner): string | null {
  switch (banner.type) {
    case FeaturedBannerType.POST:
      return (
        banner.media?.largeUrl ??
        getFirstImageSrc(banner?.post?.content || '') ??
        getVideoThumbnailUrl(banner?.post?.postMedia?.media || ({} as Media)) ??
        null
      );
    case FeaturedBannerType.CATEGORY:
      return banner.media?.largeUrl ?? banner?.category?.media?.largeUrl ?? null;
    case FeaturedBannerType.SUB_CATEGORY:
      return banner.media?.largeUrl ?? banner?.subCategory?.media?.largeUrl ?? null;
    case FeaturedBannerType.TOPIC:
      return banner.media?.largeUrl ?? null;
    case FeaturedBannerType.ANNOUNCEMENT:
      return banner.media?.largeUrl ?? null;
    case FeaturedBannerType.ADVERTISEMENT:
      return banner.media?.largeUrl ?? null;
  }
}

export function displayUserName(user: User): string {
  return user.displayFullName ? `${user.firstName} ${user.lastName}` : user.username;
}

export function getArticleUrl(post: Post): string {
  const base = removeUrlTrailingSlash(process.env.APP_BASE_URL ?? 'https://qa.ichitter.com');
  const postSlug = slug(post.title ?? '', { lower: true });
  return `${base}/post/${post.id}${postSlug ? `/${postSlug}` : ''}?showComments=true`;
}
